// RevenueTicker — hero element of Analytics' revenue section.
//
// Recognizes revenue at JOB COMPLETION, not invoice payment: each
// status='complete' job contributes its `total` on its completion date.
// The completion timestamp is determined by a fallback chain because the
// production trigger (en_route/complete stamps actual_end) only kicks in
// for jobs transitioned via the UI — seeded/imported completes lack it.
// Chain: actual_end → scheduled_date → created_at. All three exist on
// every complete job in dev, so 100% of complete jobs surface here.
//
// This sits ABOVE Analytics' existing "Revenue collected over time" chart,
// which uses paid invoices (a different lens: cash collected vs work
// recognized). Both can coexist; the ticker is the hero, the chart is the
// detail. The invoice chart can retire later if it proves redundant.
//
// Data: consumes the page's existing `analytics-field-jobs` query (already
// fetches all workspace jobs, no date filter). The page-level select was
// extended to include actual_end/scheduled_date/total so this component
// needs no additional query — all 6 tabs bucket the same in-memory rows.

import { useEffect, useMemo, useState } from 'react'
import {
  ComposedChart, Area, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid,
} from 'recharts'
import { ArrowUpRight, ArrowDownRight } from 'lucide-react'
import {
  startOfDay, endOfDay, subDays, subMonths, subYears, startOfMonth, startOfYear,
  format, eachDayOfInterval, eachMonthOfInterval, eachHourOfInterval,
  isWithinInterval,
} from 'date-fns'
import { formatCurrency } from '@/lib/utils'
import { completedAt, scheduledAt, isRecognized, isScheduled, jobValue } from '@/pages/analytics/revenueData'

type TickerRange = 'today' | '7d' | '30d' | 'mtd' | '12m' | 'ytd'

const RANGE_KEY = 'orkestra-ticker-range'
const RANGE_TABS: { id: TickerRange; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: '7d',    label: '7D' },
  { id: '30d',   label: '30D' },
  { id: 'mtd',   label: 'MTD' },
  { id: '12m',   label: '12M' },
  { id: 'ytd',   label: 'YTD' },
]

// completedAt, scheduledAt, isRecognized, isScheduled, jobValue all
// live in ./revenueData so RevenueProjections can apply the same
// semantics without divergence risk.

type Window = { start: Date; end: Date; bucketBy: 'hour' | 'day' | 'month' }

// Resolve the current period window AND the prior-equivalent comparison
// window for a given range. "Prior equivalent" is the same shape and span
// as the current window, offset back. For partial periods (Today, MTD,
// YTD) we compare against the same elapsed-time slice of the previous
// equivalent period — that way a 10am-today comparison reads against
// yesterday-up-to-10am, not yesterday's full total.
function resolveWindows(range: TickerRange, now: Date): { current: Window; prior: Window } {
  switch (range) {
    case 'today': {
      const todayStart = startOfDay(now)
      const yStart = subDays(todayStart, 1)
      // Current window extends to end-of-day so the scheduled line can
      // plot the remainder of today's booked work. Recognized total is
      // unaffected — future hours have zero completions. Prior window
      // stays capped at "yesterday up to the same elapsed time" so the
      // change indicator compares like-for-like (now-of-today vs
      // now-of-yesterday), not now-vs-full-yesterday.
      return {
        current: { start: todayStart, end: endOfDay(now), bucketBy: 'hour' },
        prior:   { start: yStart,     end: subDays(now, 1), bucketBy: 'hour' },
      }
    }
    case '7d': {
      const start = subDays(now, 7)
      return {
        current: { start,                end: now,   bucketBy: 'day' },
        prior:   { start: subDays(start, 7), end: start, bucketBy: 'day' },
      }
    }
    case '30d': {
      const start = subDays(now, 30)
      return {
        current: { start,                  end: now,   bucketBy: 'day' },
        prior:   { start: subDays(start, 30), end: start, bucketBy: 'day' },
      }
    }
    case 'mtd': {
      const monthStart = startOfMonth(now)
      const elapsedMs = now.getTime() - monthStart.getTime()
      const lastMonthStart = startOfMonth(subMonths(now, 1))
      return {
        current: { start: monthStart,     end: now,                              bucketBy: 'day' },
        prior:   { start: lastMonthStart, end: new Date(lastMonthStart.getTime() + elapsedMs), bucketBy: 'day' },
      }
    }
    case '12m': {
      const start = subMonths(now, 12)
      return {
        current: { start,                     end: now,   bucketBy: 'month' },
        prior:   { start: subMonths(start, 12), end: start, bucketBy: 'month' },
      }
    }
    case 'ytd': {
      const yearStart = startOfYear(now)
      const elapsedMs = now.getTime() - yearStart.getTime()
      const lastYearStart = startOfYear(subYears(now, 1))
      return {
        current: { start: yearStart,     end: now,                                bucketBy: 'month' },
        prior:   { start: lastYearStart, end: new Date(lastYearStart.getTime() + elapsedMs), bucketBy: 'month' },
      }
    }
  }
}

type Bucket = { date: string; recognized: number; scheduled: number; ts: number }

// Build a continuous series with TWO parallel value fields per bucket.
// Recognized = complete jobs at their completion timestamp (chain).
// Scheduled = all non-cancelled jobs at their scheduled_date — a stable
// historical record of what was BOOKED for each bucket. Complete jobs
// intentionally appear in BOTH series (recognized at completion time,
// scheduled at booking time): the past gap between the two lines is the
// slippage view (booked work that didn't land in the expected period).
// Cancelled is excluded from both (the booking evaporated). Empty
// buckets stay zero in both fields so recharts draws continuous lines
// (null/undefined would break the line).
function buildSeries(
  window: Window,
  completeJobs: any[],
  scheduledJobs: any[],
): Bucket[] {
  const labelFmt = window.bucketBy === 'hour' ? 'HH:00'
    : window.bucketBy === 'day' ? 'MMM d'
    : 'MMM yyyy'
  const keyFmt = window.bucketBy === 'hour' ? 'yyyy-MM-dd HH'
    : window.bucketBy === 'day' ? 'yyyy-MM-dd'
    : 'yyyy-MM'

  const stamps =
    window.bucketBy === 'hour' ? eachHourOfInterval({ start: window.start, end: window.end })
    : window.bucketBy === 'day' ? eachDayOfInterval({ start: window.start, end: window.end })
    : eachMonthOfInterval({ start: window.start, end: window.end })

  const map = new Map<string, Bucket>()
  for (const s of stamps) {
    const key = format(s, keyFmt)
    map.set(key, { date: format(s, labelFmt), recognized: 0, scheduled: 0, ts: s.getTime() })
  }

  const accumulate = (items: any[], dateOf: (j: any) => Date | null, field: 'recognized' | 'scheduled') => {
    for (const j of items) {
      const dt = dateOf(j)
      if (!dt) continue
      if (!isWithinInterval(dt, { start: window.start, end: window.end })) continue
      const bucket = map.get(format(dt, keyFmt))
      if (bucket) bucket[field] += jobValue(j)
    }
  }
  accumulate(completeJobs,  completedAt, 'recognized')
  accumulate(scheduledJobs, scheduledAt, 'scheduled')

  return Array.from(map.values()).sort((a, b) => a.ts - b.ts)
}

// Local tooltip — surface-raised pill matching the Analytics CustomTooltip
// pattern. Renders BOTH series with their respective colors and labels;
// recharts hands us one payload entry per series, keyed by dataKey, so we
// look them up by name rather than relying on index order.
function TickerTooltip({ active, payload, label, recognizedColor }: any) {
  if (!active || !payload?.length) return null
  const recognized = payload.find((p: any) => p.dataKey === 'recognized')?.value ?? 0
  const scheduled  = payload.find((p: any) => p.dataKey === 'scheduled')?.value  ?? 0
  return (
    <div style={{ background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 10, padding: '8px 12px', fontSize: 12, color: 'var(--ink-primary)', minWidth: 160 }}>
      <p style={{ fontSize: 11, color: 'var(--ink-secondary)', margin: '0 0 6px' }}>{label}</p>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>Recognized</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: recognizedColor }}>{formatCurrency(recognized)}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 2 }}>
        <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>Scheduled</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--info)' }}>{formatCurrency(scheduled)}</span>
      </div>
    </div>
  )
}

export default function RevenueTicker({ jobs, isMobile = false }: { jobs: any[]; isMobile?: boolean }) {
  const [range, setRange] = useState<TickerRange>(() => {
    try {
      const raw = localStorage.getItem(RANGE_KEY)
      const valid = RANGE_TABS.some(t => t.id === raw)
      return valid ? (raw as TickerRange) : '30d'
    } catch { return '30d' }
  })
  useEffect(() => {
    try { localStorage.setItem(RANGE_KEY, range) } catch { /* ignore */ }
  }, [range])

  // Series filters live in revenueData so both Ticker and Projections
  // apply identical semantics — see that file for the rationale on why
  // scheduled INCLUDES completes (stable booked-work record + slippage view).
  const completeJobs  = useMemo(() => jobs.filter(isRecognized), [jobs])
  const scheduledJobs = useMemo(() => jobs.filter(isScheduled),  [jobs])

  const { current, prior } = useMemo(() => resolveWindows(range, new Date()), [range])
  const series      = useMemo(() => buildSeries(current, completeJobs, scheduledJobs), [current, completeJobs, scheduledJobs])
  // Prior series only feeds the recognized-change indicator — the scheduled
  // line has no prior comparison (it's a forward-looking metric, not a
  // performance one), so we pass [] for scheduledJobs on the prior call.
  const priorSeries = useMemo(() => buildSeries(prior, completeJobs, []), [prior, completeJobs])

  const currentTotal   = useMemo(() => series.reduce((s, b) => s + b.recognized, 0), [series])
  const scheduledTotal = useMemo(() => series.reduce((s, b) => s + b.scheduled,  0), [series])
  const priorTotal     = useMemo(() => priorSeries.reduce((s, b) => s + b.recognized, 0), [priorSeries])

  // Change semantics — avoid divide-by-zero (renders as "—" when no prior
  // basis exists). When prior=0 and current>0 the period is "new" revenue,
  // shown with the positive treatment but no percentage. When both are
  // zero, neutral (no arrow, ink-tertiary dash).
  const delta = currentTotal - priorTotal
  const isPositive = delta >= 0
  const lineColor = isPositive ? 'var(--positive)' : 'var(--danger)'
  const changeLabel = (() => {
    if (priorTotal === 0 && currentTotal === 0) return { kind: 'neutral' as const }
    if (priorTotal === 0) return { kind: 'new' as const, abs: currentTotal }
    return {
      kind: 'pct' as const,
      pct: Math.abs((delta / priorTotal) * 100),
      abs: Math.abs(delta),
      isPositive,
    }
  })()

  // Stable gradient id per polarity so recharts doesn't blink when the
  // sign flips (would otherwise re-mount the <defs> element).
  const gradId = isPositive ? 'tickerGradPos' : 'tickerGradNeg'

  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 14, padding: isMobile ? '14px 14px 6px' : '18px 20px 8px', marginBottom: 16 }}>
      {/* Header: label + tabs */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
        <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>
          Revenue recognized (job completion)
        </p>
        <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {RANGE_TABS.map(t => (
            <button key={t.id} onClick={() => setRange(t.id)}
              style={{
                padding: '4px 10px', borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                background: range === t.id ? 'var(--accent-dim)' : 'transparent',
                color: range === t.id ? 'var(--accent-text)' : 'var(--ink-tertiary)',
                border: `1px solid ${range === t.id ? 'var(--accent-border)' : 'transparent'}`,
              }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Hero number + delta. Both inline on desktop, stacked on mobile.
          Hero number + delta indicator describe RECOGNIZED only; scheduled
          revenue is a forward-looking pipeline metric reported separately
          on the secondary stat line below. */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 4, flexWrap: 'wrap' }}>
        <p style={{ fontSize: isMobile ? 26 : 32, fontWeight: 900, color: 'var(--ink-primary)', margin: 0, letterSpacing: '-0.02em' }}>
          {formatCurrency(currentTotal)}
        </p>
        {changeLabel.kind === 'neutral' && (
          <span style={{ fontSize: 13, color: 'var(--ink-tertiary)', fontWeight: 600 }}>—</span>
        )}
        {changeLabel.kind === 'new' && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, fontWeight: 700, color: 'var(--positive)' }}>
            <ArrowUpRight style={{ width: 13, height: 13 }} />
            new · {formatCurrency(changeLabel.abs)}
          </span>
        )}
        {changeLabel.kind === 'pct' && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, fontWeight: 700, color: changeLabel.isPositive ? 'var(--positive)' : 'var(--danger)' }}>
            {changeLabel.isPositive
              ? <ArrowUpRight style={{ width: 13, height: 13 }} />
              : <ArrowDownRight style={{ width: 13, height: 13 }} />}
            {changeLabel.pct.toFixed(1)}% · {changeLabel.isPositive ? '+' : '−'}{formatCurrency(changeLabel.abs)}
          </span>
        )}
      </div>
      {/* Secondary stat — scheduled total for the same period. Separate
          line so it doesn't compete with the hero recognized number. */}
      <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: '0 0 10px' }}>
        Scheduled: <span style={{ color: 'var(--info)', fontWeight: 700 }}>{formatCurrency(scheduledTotal)}</span>
      </p>

      {/* Legend — two swatches identifying the recognized (filled) vs
          scheduled (dashed) lines. Sits just above the chart so it's
          legible on both desktop and mobile without occupying chart space. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 6, fontSize: 11, color: 'var(--ink-tertiary)' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span aria-hidden style={{ width: 14, height: 2, background: lineColor, borderRadius: 2 }} />
          Recognized
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span aria-hidden style={{ width: 14, height: 0, borderTop: '1.5px dashed var(--info)' }} />
          Scheduled
        </span>
      </div>

      {/* Composed chart — Area for recognized (filled), Line for scheduled
          (dashed, no fill). Both share the bucket array; recharts hands
          our tooltip both payload entries keyed by dataKey. */}
      <ResponsiveContainer width="100%" height={isMobile ? 140 : 180}>
        <ComposedChart data={series} margin={{ top: 0, right: 4, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor={lineColor} stopOpacity={0.25} />
              <stop offset="95%" stopColor={lineColor} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--hover-bg)" vertical={false} />
          <XAxis dataKey="date" tick={{ fill: 'var(--ink-tertiary)', fontSize: 10 }} axisLine={false} tickLine={false} minTickGap={isMobile ? 20 : 30} />
          <YAxis tick={{ fill: 'var(--ink-tertiary)', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} width={48} />
          <Tooltip content={<TickerTooltip recognizedColor={lineColor} />} />
          <Area type="monotone" dataKey="recognized" stroke={lineColor} strokeWidth={2} fill={`url(#${gradId})`} />
          <Line type="monotone" dataKey="scheduled" stroke="var(--info)" strokeWidth={1.5} strokeDasharray="4 3" dot={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}
